/**
 * PaperFillView — render a form on its designed paper canvas at fill time.
 *
 * The form's `paper_layout` controls the page size + orientation. Each
 * field with a `canvas_position` is rendered at that absolute spot,
 * showing the same Input/Select/Textarea/etc. used in the stacked view.
 * Fields without a canvas_position are stacked below the canvas so
 * nothing gets lost.
 */
import React, { useMemo } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';

const PAPER_SIZES = {
  letter:  { w: 816,  h: 1056 },
  legal:   { w: 816,  h: 1344 },
  a4:      { w: 794,  h: 1123 },
  tabloid: { w: 1056, h: 1632 },
  custom:  { w: 816,  h: 1056 },
};

function paperDims(layout) {
  const base = PAPER_SIZES[layout?.page_size] || PAPER_SIZES.letter;
  const w = layout?.page_size === 'custom' ? (layout.custom_width || base.w) : base.w;
  const h = layout?.page_size === 'custom' ? (layout.custom_height || base.h) : base.h;
  return layout?.orientation === 'landscape' ? { w: h, h: w } : { w, h };
}

export default function PaperFillView({ form, responses, setResponses, signingFieldId, setSigningFieldId, zoom = 1 }) {
  const layout = form?.paper_layout;
  const dims = useMemo(() => paperDims(layout), [layout]);
  const placedFields = (form.fields || []).filter(f => f.canvas_position);
  const stackedFields = (form.fields || []).filter(f => !f.canvas_position);

  const setResp = (fid, v) => setResponses(prev => ({ ...prev, [fid]: v }));

  return (
    <div className="space-y-6" data-testid="paper-fill-view">
      <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg p-4 overflow-auto">
        <div style={{ width: dims.w * zoom, height: dims.h * zoom, margin: '0 auto' }}>
          <div
            className="bg-white shadow-md relative"
            style={{
              width: dims.w,
              height: dims.h,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
          >
            {placedFields.map((f) => {
              const pos = f.canvas_position;
              const value = responses[f.field_id];
              return (
                <div
                  key={f.field_id}
                  className="absolute"
                  style={{ left: pos.x, top: pos.y, width: pos.w, height: pos.h }}
                  data-testid={`paper-fill-${f.field_id}`}
                >
                  <PaperInput field={f} value={value} setValue={(v) => setResp(f.field_id, v)} responses={responses} fields={form.fields || []} onSign={() => setSigningFieldId && setSigningFieldId(f.field_id)} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {stackedFields.length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-4">
          <div className="text-xs font-medium text-[#92400E] mb-2">
            {stackedFields.length} field{stackedFields.length === 1 ? '' : 's'} not placed on the canvas — fill below:
          </div>
          <div className="space-y-3">
            {stackedFields.map((f) => (
              <div key={f.field_id} className="bg-white rounded p-3 border border-[#FCD34D]" data-testid={`paper-stacked-${f.field_id}`}>
                <div className="text-xs font-medium text-[#0A2342] mb-1">
                  {f.label || `(${f.field_type})`}{f.required && <span className="text-[#991B1B]">*</span>}
                </div>
                <PaperInput field={f} value={responses[f.field_id]} setValue={(v) => setResp(f.field_id, v)} responses={responses} fields={form.fields || []} onSign={() => setSigningFieldId && setSigningFieldId(f.field_id)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PaperInput({ field, value, setValue, responses, fields, onSign }) {
  const t = field.field_type;
  if (t === 'info') {
    return <div className="text-xs text-[#0A2342] whitespace-pre-wrap">{field.info_text || field.label}</div>;
  }
  if (t === 'field_reference') {
    const src = fields.find(x => x.field_id === field.reference_field_id);
    const refVal = src ? responses[src.field_id] : null;
    let display = '';
    if (refVal === null || refVal === undefined || refVal === '') display = '—';
    else if (Array.isArray(refVal)) display = refVal.join(', ');
    else display = String(refVal);
    return <div className="text-xs text-[#0A2342] font-medium border-b border-dashed border-[#94A3B8] pb-0.5">{display}</div>;
  }
  if (t === 'textarea') {
    return <Textarea value={value || ''} onChange={(e) => setValue(e.target.value)} className="w-full h-full text-xs" />;
  }
  if (t === 'select') {
    return (
      <Select value={value || ''} onValueChange={setValue}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger>
        <SelectContent>{(field.options || []).map((o, i) => <SelectItem key={i} value={String(o)} className="text-xs">{String(o)}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (t === 'radio') {
    return (
      <div className="flex flex-wrap gap-2">
        {(field.options || []).map((o, i) => (
          <label key={i} className="text-xs flex items-center gap-1">
            <input type="radio" name={field.field_id} checked={value === o} onChange={() => setValue(o)} />
            {String(o)}
          </label>
        ))}
      </div>
    );
  }
  if (t === 'checkbox') {
    const arr = Array.isArray(value) ? value : [];
    const opts = field.options && field.options.length ? field.options : [field.label || 'Yes'];
    return (
      <div className="flex flex-wrap gap-2">
        {opts.map((o, i) => (
          <label key={i} className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={arr.includes(o)} onChange={(e) => setValue(e.target.checked ? [...arr, o] : arr.filter(x => x !== o))} />
            {String(o)}
          </label>
        ))}
      </div>
    );
  }
  if (t === 'signature') {
    return (
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onSign}>
        {value ? '✓ Signed (tap to re-sign)' : 'Tap to sign'}
      </Button>
    );
  }
  // text, number, date, email, phone — default
  const inputType = t === 'number' ? 'number' : t === 'date' ? 'date' : t === 'email' ? 'email' : t === 'phone' ? 'tel' : 'text';
  return (
    <Input
      type={inputType}
      value={value || ''}
      onChange={(e) => setValue(e.target.value)}
      placeholder={field.placeholder}
      className="h-8 text-xs"
    />
  );
}
